// =============================================================================
// ProjectTasksDialog — manage the per-project task definitions used by the
// timesheet "Log Time" dropdown (e.g. "Sub Link – BAU", "Sub Link – Major").
// Scoped to one client+project (bo_id + cl_id + proj_id).
// =============================================================================
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TextField, Button, Box, Typography, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Snackbar, Alert, Tooltip, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ProjectTasksDialog({ open, onClose, bo_id, cl_id, proj_id, proj_name }) {
  const { empId } = useCurrentUser();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  // add/edit form
  const [editingId, setEditingId] = useState(null); // null = add mode
  const [taskName, setTaskName] = useState("");
  const [taskType, setTaskType] = useState("");

  const showSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  const fetchTasks = useCallback(async () => {
    if (!bo_id || cl_id === undefined || cl_id === null || !proj_id) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/project-tasks/${bo_id}/${cl_id}/${proj_id}`);
      const data = res.ok ? await res.json() : { data: [] };
      setTasks(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [bo_id, cl_id, proj_id]);

  useEffect(() => {
    if (open) {
      fetchTasks();
      setEditingId(null);
      setTaskName("");
      setTaskType("");
    }
  }, [open, fetchTasks]);

  const resetForm = () => {
    setEditingId(null);
    setTaskName("");
    setTaskType("");
  };

  const handleSave = async () => {
    if (!taskName.trim()) {
      showSnack("Task name is required", "error");
      return;
    }
    if (!bo_id || cl_id === undefined || cl_id === null || !proj_id) {
      showSnack("Project reference is missing. Please reopen this dialog.", "error");
      return;
    }
    try {
      if (editingId) {
        const res = await fetch(`${BASE_URL}/project-tasks/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            task_name: taskName.trim(),
            task_type: taskType.trim() || null,
            modified_by: empId,
          }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Update failed");
        showSnack("Task updated");
      } else {
        const res = await fetch(`${BASE_URL}/project-tasks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bo_id, cl_id, proj_id,
            task_name: taskName.trim(),
            task_type: taskType.trim() || null,
            created_by: empId,
          }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Add failed");
        showSnack("Task added");
      }
      resetForm();
      fetchTasks();
    } catch (err) {
      showSnack(err.message || "Save failed", "error");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.task_id);
    setTaskName(t.task_name || "");
    setTaskType(t.task_type || "");
  };

  const handleDelete = async (task_id) => {
    try {
      const res = await fetch(`${BASE_URL}/project-tasks/${task_id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      showSnack("Task deleted");
      if (editingId === task_id) resetForm();
      fetchTasks();
    } catch (err) {
      showSnack(err.message || "Delete failed", "error");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #EBECF0",
          pb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#172B4D" }}>
            Manage Tasks
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#5E6C84" }}>
            {proj_name || `Project ${proj_id}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#5E6C84" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Add / edit form */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 1, mb: 2 }}>
          <TextField
            label="Task Name"
            size="small"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            placeholder="e.g. Dev / app"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Type (optional)"
            size="small"
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            placeholder="e.g. BAU / Major / Bug"
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={editingId ? <EditOutlinedIcon /> : <AddIcon />}
            onClick={handleSave}
            sx={{
              height: 40,
              whiteSpace: "nowrap",
              bgcolor: "#0052CC",
              "&:hover": { bgcolor: "#0065FF" },
              textTransform: "none",
            }}
          >
            {editingId ? "Update" : "Add"}
          </Button>
          {editingId && (
            <Button
              variant="outlined"
              onClick={resetForm}
              sx={{ height: 40, textTransform: "none" }}
            >
              Cancel
            </Button>
          )}
        </Stack>

        {/* Existing tasks */}
        <Paper
          elevation={0}
          sx={{ border: "1px solid #EBECF0", borderRadius: 1, overflow: "hidden" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F4F5F7" }}>
                <TableCell sx={{ fontWeight: 700, color: "#5E6C84", fontSize: "0.75rem" }}>
                  Task Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#5E6C84", fontSize: "0.75rem" }}>
                  Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#5E6C84", fontSize: "0.75rem" }}>
                  Preview
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#5E6C84", fontSize: "0.75rem" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#97A0AF" }}>
                    No tasks defined for this project yet.
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((t) => (
                  <TableRow key={t.task_id} sx={{ "&:hover": { bgcolor: "#F8F9FA" } }}>
                    <TableCell sx={{ fontSize: "0.82rem" }}>{t.task_name}</TableCell>
                    <TableCell sx={{ fontSize: "0.82rem", color: "#5E6C84" }}>
                      {t.task_type || "—"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#0052CC", fontSize: "0.82rem" }}>
                      {t.display_label || t.task_name}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(t)}
                          sx={{ color: "#2DB0E4" }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(t.task_id)}
                          sx={{ color: "#DE350B" }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #EBECF0", px: 2, py: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: "none", color: "#5E6C84", borderColor: "#DFE1E6" }}
        >
          Close
        </Button>
      </DialogActions>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          sx={{ width: "100%" }}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
